import { MoveUpRight, MoveDownRight, MoveRight } from "lucide-react";

export function calculateTrendLine<T extends Record<string, unknown>>(data: T[], dataKey: string): T[] {
    const points = data
        .map((item, index) => ({ x: index, y: item[dataKey] as number }))
        .filter((point) => typeof point.y === "number" && !isNaN(point.y));
    if (points.length < 2) {
        return data.map((item) => ({ ...item, [`${dataKey}_trend`]: item[dataKey] }));
    }
    const n = points.length;
    const sumX = points.reduce((s, p) => s + p.x, 0);
    const sumY = points.reduce((s, p) => s + p.y, 0);
    const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
    const sumXX = points.reduce((s, p) => s + p.x * p.x, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    return data.map((item, index) => ({ ...item, [`${dataKey}_trend`]: slope * index + intercept }));
}

export function getMatchInfo(
    homeTeam: string,
    awayTeam: string,
    homeGoals: number,
    awayGoals: number,
    playerTeamId?: number,
    homeTeamId?: number,
    awayTeamId?: number
) {
    const isHome =
        playerTeamId !== undefined && (homeTeamId !== undefined || awayTeamId !== undefined)
            ? playerTeamId === homeTeamId
            : homeTeam === " ";
    const opponent = isHome ? awayTeam : homeTeam;
    const vg = isHome ? homeGoals : awayGoals;
    const og = isHome ? awayGoals : homeGoals;
    const result: "W" | "D" | "L" = vg > og ? "W" : vg < og ? "L" : "D";
    return { opponent, result, score: `${homeGoals}-${awayGoals}` };
}

export const KPI_COLOR_MAP: Record<string, string> = {
    blue: "var(--color-chart-blue)",
    green: "var(--color-chart-green)",
    red: "#dc2626",
    yellow: "var(--color-medal-gold)",
    purple: "#9333ea",
    orange: "#ea580c",
    cyan: "#06b6d4",
};

export function resolveKpiColor(colorName?: string): string {
    return KPI_COLOR_MAP[colorName || "blue"] || "var(--color-chart-blue)";
}

export interface KpiOption {
    value: string;
    label: string;
    fullLabel: string;
    color: string;
}

interface TrendLabelProps {
    x?: string | number;
    y?: string | number;
    value?: string | number | null | boolean;
    index?: number;
    dataKey?: string;
    data?: Record<string, unknown>[];
    invertTrend?: boolean;
}

export function TrendLabel({ x, y, value, index, dataKey, data, invertTrend = false }: TrendLabelProps) {
    if ((!value && value !== 0) || index === undefined || !data || !x || !y) return null;
    const xCoord = typeof x === "string" ? parseFloat(x) : x;
    const yCoord = typeof y === "string" ? parseFloat(y) : y;
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (typeof numValue !== "number" || isNaN(xCoord) || isNaN(yCoord) || isNaN(numValue)) return null;

    const previousValue = index > 0 && dataKey ? data[index - 1]?.[dataKey] : null;
    let trend: "up" | "down" | "same" | null = null;
    if (typeof previousValue === "number") {
        if (numValue > previousValue) trend = "up";
        else if (numValue < previousValue) trend = "down";
        else trend = "same";
    }

    const trendIcon =
        trend === "up" ? (
            <MoveUpRight className="w-4 h-4" />
        ) : trend === "down" ? (
            <MoveDownRight className="w-4 h-4" />
        ) : trend === "same" ? (
            <MoveRight className="w-4 h-4" />
        ) : null;

    const upColor = invertTrend ? "var(--color-general-red)" : "var(--color-general-green)";
    const downColor = invertTrend ? "var(--color-general-green)" : "var(--color-general-red)";
    const trendColor =
        trend === "up" ? upColor : trend === "down" ? downColor : trend === "same" ? "var(--color-gray-500)" : "gray";

    const displayValue = numValue % 1 === 0 ? numValue.toString() : numValue.toFixed(1);

    return (
        <g transform={`translate(${xCoord || 0}, ${yCoord || 0})`}>
            <text
                x={0}
                y={-15}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={trendColor}
                fontSize={14}
                fontWeight="bold"
                style={{ userSelect: "none" }}
            >
                {displayValue}
            </text>
            {trend && (
                <foreignObject x={-10} y={-40} width={20} height={20} style={{ overflow: "visible" }}>
                    <div
                        style={{
                            color: trendColor,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        {trendIcon}
                    </div>
                </foreignObject>
            )}
        </g>
    );
}
